import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

/**
 * ListSearchBar — the filter row for a CRM list view.
 *
 * Lifted from pages/tickets/Tickets.jsx, which was the only list page that had
 * an inline search box at all. /mids and /mid-applications put search behind the
 * Filters drawer, so finding a client — the single most frequent action on both
 * pages — cost two clicks and a panel. Both endpoints already accept `search`
 * server-side, so this was purely a missing control.
 *
 * The debounce is the reason this is a component rather than copied markup:
 * typing writes to a PERSISTED filter (URL + storage via useSearch), so an
 * un-debounced box would push a history entry per keystroke and refetch on each.
 *
 * `children` renders after the input for page-specific inline controls
 * (status/priority selects, segmented tabs). Anything that needs to reach across
 * pages belongs here; anything page-specific stays in the page.
 */
export default function ListSearchBar({
  value = "",
  onChange,
  placeholder = "Search",
  delay = 300,
  children,
  className = "",
}) {
  const [text, setText] = useState(value || "");

  // Held in a ref so an inline arrow from the caller doesn't re-arm the timer on
  // every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Debounce local typing into the persisted filter.
  useEffect(() => {
    const id = setTimeout(() => {
      if ((value || "") !== text) onChangeRef.current?.(text);
    }, delay);
    return () => clearTimeout(id);
  }, [text, value, delay]);

  // Keep the box in sync when the filter changes from elsewhere — a filter
  // reset, or landing on the page with a search already in the URL.
  useEffect(() => {
    setText(value || "");
  }, [value]);

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="input pl-9"
        />
      </div>
      {children}
    </div>
  );
}
